export const STALE_MODULE_RELOAD_KEY = 'ruzuku:staleModuleReloadAttempt';

export const PENDING_CHUNK_RELOAD_KEY = 'ruzuku:pendingChunkReload';

/**
 * How long to wait before each reload of the same page. While a deploy swaps
 * instances, old and new ones both serve requests for about a minute, so a
 * page from a new instance can ask an old one for a file it doesn't have.
 * Later reloads wait for the old instances to stop.
 */
export const STALE_MODULE_RELOAD_DELAYS_MS = [0, 5_000, 15_000];

/**
 * A page that ran out of reloads gets a fresh set once this much time has
 * passed since its last reload, e.g. when the next deploy breaks it again.
 */
export const STALE_MODULE_RELOAD_RESET_MS = 2 * 60_000;

export type StaleModuleReloadAttempt = {
  url: string;
  count: number;
  at: number;
};

export type PendingChunkReload = {
  context: string;
  at: number;
  url: string;
  attempt?: number;
  message?: string;
};

/**
 * Decides whether a page whose JavaScript failed to load should reload again.
 * Returns the attempt to record, or undefined when this page has used all its
 * reloads recently and the error should be reported instead.
 */
export function nextStaleModuleReload(
  last: StaleModuleReloadAttempt | undefined,
  url: string,
  now: number,
): StaleModuleReloadAttempt | undefined {
  if (!last || last.url !== url || now - last.at > STALE_MODULE_RELOAD_RESET_MS) {
    return { url, count: 1, at: now };
  }
  if (last.count >= STALE_MODULE_RELOAD_DELAYS_MS.length) {
    return;
  }
  return { url, count: last.count + 1, at: now };
}

export function staleModuleReloadDelay(attempt: StaleModuleReloadAttempt) {
  return STALE_MODULE_RELOAD_DELAYS_MS[attempt.count - 1] ?? 0;
}

export function parseStaleModuleReloadAttempt(
  stored: string | null | undefined,
): StaleModuleReloadAttempt | undefined {
  if (!stored) {
    return;
  }
  try {
    const parsed = JSON.parse(stored) as Partial<StaleModuleReloadAttempt>;
    if (
      typeof parsed?.url === 'string' &&
      typeof parsed.count === 'number' &&
      typeof parsed.at === 'number'
    ) {
      return { url: parsed.url, count: parsed.count, at: parsed.at };
    }
  } catch {
    return;
  }
}
